import type { CSSProperties } from 'react'
import type { SelectOption } from '@/components/SearchableSelect'
import type {
  PurchasingMeezIngredientRow,
  PurchasingNewProductInput,
  PurchasingProductRow,
  PurchasingPurchaseLineRow,
  PurchasingReviewQueueRow,
  PurchasingReviewQueueSuggestion,
} from '@/services/purchasingTypes'
import type { PurchasingPackRulingRequest, PurchasingVendorItemMatchRequest } from '@/services/purchasing'
import type { FieldDefinition, FieldOption, SortSpec } from '@/tableControls'

export const PURCHASING_REVIEW_PAGE_KEY = 'purchasing-review'

export const PACK_DRIFT_FLAG = 'pack_drift'
export const MODEL_PATH_UNCONFIRMED_FLAG = 'model_path_unconfirmed'
export const ALIAS_PACK_UNCONFIRMED_FLAG = 'alias_pack_unconfirmed'
export const ITEM_ID_MATCH_FLAG = 'item_id_match'

export const CONFIRMABLE_REASONS = new Set([
  'qty_assumed',
  'cost_outlier',
  ALIAS_PACK_UNCONFIRMED_FLAG,
])

/** Reasons that block Approve until resolved another way (pack dialog, link, etc.). */
export const BLOCKING_REASONS = new Set([
  'missing_atomic_cost',
  'pack_drift',
  'uom_conflict',
  'no_row',
  'amount_mismatch',
  'enrichment_failed',
])

const NON_BLOCKING_REASONS = new Set([
  ...CONFIRMABLE_REASONS,
  'needs_product',
  'flagged',
])

const INFORMATIONAL_REVIEW_FLAGS = new Set([
  'model_path_corrected',
  MODEL_PATH_UNCONFIRMED_FLAG,
  'first_seen_flag',
  'repeat_flag',
])

export function partitionReviewFlags(
  flags: string[] | null | undefined,
): { unresolved: string[]; informational: string[] } {
  const unresolved: string[] = []
  const informational: string[] = []
  for (const flag of flags ?? []) {
    if (INFORMATIONAL_REVIEW_FLAGS.has(flag)) informational.push(flag)
    else unresolved.push(flag)
  }
  return { unresolved, informational }
}

export function rowHasConfirmedProduct(row: PurchasingReviewQueueRow): boolean {
  return (
    row.product_id != null &&
    (row.match_status === 'confirmed' || row.match_status === 'auto')
  )
}

export function linkedProductSelectOptionForRow(
  row: PurchasingReviewQueueRow,
): SelectOption | null {
  if (row.product_id != null && row.product_name) {
    return productRowToSelectOption({
      id: row.product_id,
      name: row.product_name,
      atomic_uom: row.product_atomic_uom ?? 'each',
    })
  }
  return null
}

export function productLinkConfirmButtonState(
  row: PurchasingReviewQueueRow,
  optionId: string | null,
): { confirmDisabled: boolean; idleLabel: string; busyLabel: string } {
  const labels = productConfirmButtonLabel(optionId)
  if (
    rowHasConfirmedProduct(row) &&
    row.product_id != null &&
    optionId === `product:${row.product_id}`
  ) {
    return { confirmDisabled: true, idleLabel: 'Linked', busyLabel: labels.busy }
  }
  return {
    confirmDisabled: !canConfirmProductLink(row, optionId),
    idleLabel: labels.idle,
    busyLabel: labels.busy,
  }
}

export function vendorItemNeedsProduct(row: PurchasingReviewQueueRow): boolean {
  const matchStatus = row.match_status
  if (matchStatus === 'confirmed' || matchStatus === 'auto') {
    return false
  }
  if (matchStatus === 'pending' || matchStatus === 'error' || matchStatus === 'rejected') {
    return true
  }
  return row.product_id == null
}

const ATOMIC_UOMS = new Set(['g', 'ml', 'each'])

const WEIGHT_TO_G = new Set(['OZ', 'LB', '#', 'G', 'GR', 'KG'])
const VOLUME_TO_ML = new Set(['FL OZ', 'FLOZ', 'PT', 'QT', 'GAL', 'L', 'ML'])
const EACH_FACTORS = new Set(['CT', 'EA', 'EACH', 'PC', 'DZ', 'DZN', 'CASE'])

function normalizeUomToken(uom: string): string {
  return uom.trim().toUpperCase().split(/\s+/).join(' ')
}

/** Map any UOM token to canonical atomic family g/ml/each (null when blank). */
export function canonicalAtomicUom(uom: string | null | undefined): string | null {
  if (!uom || !uom.trim()) return null
  const lowered = uom.trim().toLowerCase()
  if (ATOMIC_UOMS.has(lowered)) return lowered
  const normalized = normalizeUomToken(uom)
  if (normalized.startsWith('#')) return 'g'
  if (WEIGHT_TO_G.has(normalized) || normalized === 'OZ') return 'g'
  if (VOLUME_TO_ML.has(normalized)) return 'ml'
  if (EACH_FACTORS.has(normalized)) return 'each'
  return 'each'
}

/** Same rule as match_actions._uom_conflicts. */
export function uomsConflict(
  itemUom: string | null | undefined,
  productUom: string | null | undefined,
): boolean {
  if (itemUom == null) return false
  const packCanonical = canonicalAtomicUom(itemUom)
  const productCanonical = canonicalAtomicUom(productUom)
  if (productCanonical == null) return false
  return packCanonical !== productCanonical
}

export function mapMeezUomToAtomic(
  uom: string | null | undefined,
  fallback?: string | null,
): string {
  const canonical = canonicalAtomicUom(uom)
  if (canonical) return canonical
  const fallbackCanonical = canonicalAtomicUom(fallback)
  if (fallbackCanonical) return fallbackCanonical
  return 'each'
}

type ProductUomLookup = Pick<PurchasingProductRow, 'id' | 'atomic_uom'>
type MeezUomLookup = Pick<PurchasingMeezIngredientRow, 'id' | 'uom'>

function meezUomForSelection(
  meezIngredientId: string,
  row: PurchasingReviewQueueRow,
  meezRows: MeezUomLookup[] = [],
): string | null | undefined {
  const fromCatalog = meezRows.find((entry) => entry.id === meezIngredientId)?.uom
  if (fromCatalog !== undefined) return fromCatalog
  if (row.suggestion?.meez_ingredient_id === meezIngredientId) {
    return row.suggestion.meez_uom
  }
  return undefined
}

export function resolveRowProductAtomicUom(
  row: PurchasingReviewQueueRow,
  selection: ReviewSelection | null,
  products: ProductUomLookup[] = [],
  meezRows: MeezUomLookup[] = [],
): string | null {
  if (selection?.kind === 'product') {
    const product = products.find((entry) => entry.id === selection.productId)
    if (product) return product.atomic_uom
  }
  if (selection?.kind === 'meez') {
    const meezUom = meezUomForSelection(selection.meezIngredientId, row, meezRows)
    return mapMeezUomToAtomic(meezUom, row.atomic_uom)
  }
  if (selection?.kind === 'new_product') {
    return selection.newProduct.atomic_uom
  }
  if (row.product_atomic_uom != null) return row.product_atomic_uom
  if (row.product_id != null) {
    const product = products.find((entry) => entry.id === row.product_id)
    if (product) return product.atomic_uom
  }
  const suggestion = row.suggestion
  if (suggestion?.proposed_product_id != null) {
    const suggestedId = suggestion.proposed_product_id
    const matchesCurrentOption =
      selection == null ||
      (selection.kind === 'product' && selection.productId === suggestedId) ||
      row.product_id === suggestedId
    if (matchesCurrentOption) {
      if (suggestion.atomic_uom) return suggestion.atomic_uom
      const product = products.find((entry) => entry.id === suggestedId)
      if (product) return product.atomic_uom
    }
  }
  if (suggestion?.meez_ingredient_id) {
    return mapMeezUomToAtomic(suggestion.meez_uom, row.atomic_uom)
  }
  if (suggestion?.new_product?.atomic_uom) {
    return suggestion.new_product.atomic_uom
  }
  return null
}

export function queueRowReasons(
  row: PurchasingReviewQueueRow,
  selection: ReviewSelection | null,
  products: ProductUomLookup[] = [],
  meezRows: MeezUomLookup[] = [],
): string[] {
  const reasons = row.reasons ?? buildReasonsForQueueRow(row)
  const productUom = resolveRowProductAtomicUom(row, selection, products, meezRows)
  if (uomsConflict(row.atomic_uom, productUom) && !reasons.includes('uom_conflict')) {
    return [...reasons, 'uom_conflict']
  }
  return reasons
}

/** Mirror purchasing_catalog._build_reasons for local queue row updates. */
export function buildReasonsForQueueRow(row: PurchasingReviewQueueRow): string[] {
  let { unresolved } = partitionReviewFlags(row.review_flags)
  if (row.review_status === 'flagged' && unresolved.length === 0 && (row.review_flags ?? []).length === 0) {
    unresolved = ['flagged']
  }
  const reasons = [...unresolved]
  if (vendorItemNeedsProduct(row) && !reasons.includes('needs_product')) {
    reasons.push('needs_product')
  }
  return reasons
}

export function rowBelongsOnNeedsReview(row: PurchasingReviewQueueRow): boolean {
  return buildReasonsForQueueRow(row).length > 0
}

export function dropVendorItemRowsLeavingNeedsReview(
  rows: PurchasingReviewQueueRow[],
  vendorItemId: number,
): PurchasingReviewQueueRow[] {
  return rows.filter(
    (row) => row.vendor_item_id !== vendorItemId || rowBelongsOnNeedsReview(row),
  )
}

export type ProductLinkOptimisticContext = {
  products: Array<Pick<PurchasingProductRow, 'id' | 'name' | 'atomic_uom'>>
  meezRows?: Array<Pick<PurchasingMeezIngredientRow, 'id' | 'name' | 'uom'>>
  optionLabel?: string | null
}

export function buildOptimisticProductLinkPatch(
  row: PurchasingReviewQueueRow,
  payload: PurchasingVendorItemMatchRequest,
  ctx: ProductLinkOptimisticContext,
): Partial<PurchasingReviewQueueRow> {
  const patch: Partial<PurchasingReviewQueueRow> = { match_status: 'confirmed' }
  let productAtomicUom: string | null = row.product_atomic_uom

  if (payload.product_id != null) {
    patch.product_id = payload.product_id
    const product = ctx.products.find((entry) => entry.id === payload.product_id)
    productAtomicUom = product?.atomic_uom ?? null
    patch.product_atomic_uom = productAtomicUom
    patch.product_name =
      product?.name ?? ctx.optionLabel ?? row.product_name ?? row.suggestion?.product_name ?? null
  }

  if (payload.meez_ingredient_id != null) {
    patch.meez_ingredient_id = payload.meez_ingredient_id
    const meezRow = ctx.meezRows?.find((entry) => entry.id === payload.meez_ingredient_id)
    const meezUom =
      meezRow?.uom ??
      (row.suggestion?.meez_ingredient_id === payload.meez_ingredient_id
        ? row.suggestion.meez_uom
        : undefined)
    productAtomicUom = mapMeezUomToAtomic(meezUom, row.atomic_uom)
    patch.product_atomic_uom = productAtomicUom
    patch.meez_ingredient_name =
      meezRow?.name ??
      (row.suggestion?.meez_ingredient_id === payload.meez_ingredient_id
        ? row.suggestion.meez_ingredient_name
        : null) ??
      row.meez_ingredient_name
    if (patch.product_name == null) {
      patch.product_name = patch.meez_ingredient_name ?? ctx.optionLabel ?? row.product_name
    }
  }

  const flags = [...(row.review_flags ?? [])]
  if (uomsConflict(row.atomic_uom, productAtomicUom)) {
    if (!flags.includes('uom_conflict')) flags.push('uom_conflict')
    patch.review_flags = flags
  } else {
    patch.review_flags = flags.filter((flag) => flag !== 'uom_conflict')
  }

  return patch
}

export function applyOptimisticProductLinkToQueue(
  rows: PurchasingReviewQueueRow[],
  vendorItemId: number,
  patch: Partial<PurchasingReviewQueueRow>,
  segment: 'needs_review' | 'done',
): { rows: PurchasingReviewQueueRow[]; removedLineIds: number[] } {
  const patched = applyVendorItemMatchLocally(rows, vendorItemId, patch)
  if (segment !== 'needs_review') {
    return { rows: patched, removedLineIds: [] }
  }
  const beforeIds = rows
    .filter((row) => row.vendor_item_id === vendorItemId)
    .map((row) => row.id)
  const next = dropVendorItemRowsLeavingNeedsReview(patched, vendorItemId)
  const afterIdSet = new Set(next.map((row) => row.id))
  const removedLineIds = beforeIds.filter((id) => !afterIdSet.has(id))
  return { rows: next, removedLineIds }
}

export const REASON_LABELS: Record<string, string> = {
  needs_product: 'Needs product',
  missing_atomic_cost: 'Missing $/atomic',
  cost_outlier: 'Cost outlier',
  qty_assumed: 'Qty assumed',
  uom_conflict: 'UOM conflict',
  pack_drift: 'Pack drift',
  alias_pack_unconfirmed: 'Alias pack',
  [ITEM_ID_MATCH_FLAG]: 'Item ID match',
}

export const REVIEW_QUEUE_COLUMN_KEYS = [
  'description',
  'vendor_item_code',
  'vendor',
  'pack',
  'product',
  'confidence',
  'invoice',
  'quantity',
  'unit_price',
  'amount',
  'cost_per_atomic',
  'reasons',
  'approved',
  'updated',
] as const

export type ReviewQueueColumnKey = (typeof REVIEW_QUEUE_COLUMN_KEYS)[number]

export const REVIEW_QUEUE_DEFAULT_WIDTHS: Record<string, number> = {
  vendor: 120,
  vendor_item_code: 100,
  description: 180,
  pack: 100,
  product: 220,
  confidence: 88,
  invoice: 120,
  quantity: 72,
  unit_price: 88,
  amount: 88,
  cost_per_atomic: 96,
  reasons: 140,
  approved: 180,
  updated: 96,
}

export const REVIEW_QUEUE_COLUMN_LABELS: Record<ReviewQueueColumnKey, string> = {
  vendor: 'Vendor',
  vendor_item_code: 'Item code',
  description: 'Vendor Item Name',
  pack: 'Pack',
  product: 'Product',
  confidence: 'Confidence',
  invoice: 'Invoice',
  quantity: 'Qty',
  unit_price: 'Unit $',
  amount: 'Amount',
  cost_per_atomic: '$/atomic',
  reasons: 'Reasons',
  approved: 'Approved',
  updated: 'Updated',
}

export const REVIEW_GROUP_BY_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'vendor_item_code', label: 'Item code' },
  { value: 'description', label: 'Vendor Item Name' },
  { value: 'product', label: 'Product/Ingredient' },
  { value: 'reason', label: 'Reason' },
] as const

export type ReviewGroupBy = (typeof REVIEW_GROUP_BY_OPTIONS)[number]['value']

export const MATCH_STATUS_FILTER_OPTIONS: FieldOption[] = [
  { value: 'pending', label: 'Pending review' },
  { value: 'auto', label: 'Auto-linked' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'error', label: 'Match error' },
]

export const HAS_SUGGESTION_FILTER_OPTIONS: FieldOption[] = [
  { value: 'yes', label: 'Has suggestion' },
  { value: 'no', label: 'No suggestion' },
]

/** Sort field used when group-by is active — orders group headers by row count. */
export const REVIEW_GROUP_COUNT_SORT_FIELD = '__group_count'

export const REVIEW_GROUP_COUNT_FIELD_DEFINITION: FieldDefinition = {
  key: REVIEW_GROUP_COUNT_SORT_FIELD,
  label: 'Group size',
  type: 'number',
  sortable: true,
  filterable: false,
}

export function buildReviewQueueSortFields(groupBy: ReviewGroupBy): FieldDefinition[] {
  if (groupBy === 'none') return REVIEW_QUEUE_FIELD_DEFINITIONS
  return [...REVIEW_QUEUE_FIELD_DEFINITIONS, REVIEW_GROUP_COUNT_FIELD_DEFINITION]
}

export function partitionReviewSortSpecs(sortSpecs: SortSpec[]): {
  rowSorts: SortSpec[]
  groupCountSort: SortSpec | null
} {
  const rowSorts: SortSpec[] = []
  let groupCountSort: SortSpec | null = null
  for (const spec of sortSpecs) {
    if (spec.field === REVIEW_GROUP_COUNT_SORT_FIELD) {
      if (!groupCountSort) groupCountSort = spec
    } else {
      rowSorts.push(spec)
    }
  }
  return { rowSorts, groupCountSort }
}

export type ReviewSelection =
  | { kind: 'product'; productId: number }
  | { kind: 'meez'; meezIngredientId: string }
  | { kind: 'new_product'; newProduct: PurchasingNewProductInput; meezIngredientId?: string }

export function productRowToSelectOption(
  product: Pick<PurchasingProductRow, 'id' | 'name' | 'atomic_uom'>,
): SelectOption {
  return {
    id: `product:${product.id}`,
    label: product.name,
    group: 'Products',
  }
}

export function meezRowToSelectOption(
  row: Pick<PurchasingMeezIngredientRow, 'id' | 'name' | 'uom'>,
): SelectOption {
  return {
    id: `meez:${row.id}`,
    label: row.name,
    group: 'Meez ingredients',
  }
}

export function resolveMeezIngredientLabel(
  meezIngredientId: string,
  meezCatalog: Pick<PurchasingMeezIngredientRow, 'id' | 'name'>[],
  fallbackName?: string | null,
): string | null {
  const fromCatalog = meezCatalog.find((row) => row.id === meezIngredientId)?.name
  if (fromCatalog) return fromCatalog
  if (meezCatalog.length > 0) return null
  return fallbackName ?? null
}

export function meezSuggestionAutoSelectAllowed(
  meezName: string | null | undefined,
  vendorItemCode: string | null | undefined,
): boolean {
  if (!meezName) return false
  const bracketTexts = extractMeezBracketTexts(meezName)
  if (bracketTexts.length === 0) return true
  const code = (vendorItemCode ?? '').trim()
  if (code.length < 3) return true
  return vendorItemCodeMatchesMeezBracketIds(vendorItemCode, meezName)
}

export function suggestionToSelectOption(
  suggestion: PurchasingReviewQueueSuggestion,
  meezCatalog: Pick<PurchasingMeezIngredientRow, 'id' | 'name'>[] = [],
): SelectOption | null {
  if (suggestion.meez_ingredient_id) {
    const label = resolveMeezIngredientLabel(
      suggestion.meez_ingredient_id,
      meezCatalog,
      suggestion.meez_ingredient_name,
    )
    if (!label) return null
    return {
      id: `meez:${suggestion.meez_ingredient_id}`,
      label,
      group: 'Meez ingredients',
    }
  }
  if (suggestion.proposed_product_id != null && suggestion.product_name) {
    return {
      id: `product:${suggestion.proposed_product_id}`,
      label: suggestion.product_name,
      group: 'Products',
    }
  }
  return null
}

export function mergeReviewSelectOptions(...lists: SelectOption[][]): SelectOption[] {
  const seen = new Set<string>()
  const result: SelectOption[] = []
  for (const list of lists) {
    for (const opt of list) {
      if (!seen.has(opt.id)) {
        seen.add(opt.id)
        result.push(opt)
      }
    }
  }
  return result
}

export function buildReviewCatalogOptionsForRow(
  row: PurchasingReviewQueueRow,
  fetchedCatalogOptions: SelectOption[],
  createdProductOptions: SelectOption[],
  meezCatalog: Pick<PurchasingMeezIngredientRow, 'id' | 'name'>[] = [],
): SelectOption[] {
  const suggestionOpt = row.suggestion
    ? suggestionToSelectOption(row.suggestion, meezCatalog)
    : null
  const linkedOpt = linkedProductSelectOptionForRow(row)
  return mergeReviewSelectOptions(
    linkedOpt ? [linkedOpt] : [],
    suggestionOpt ? [suggestionOpt] : [],
    fetchedCatalogOptions,
    createdProductOptions,
  )
}

export function buildPurchasingReviewExportMatrix(rows: PurchasingReviewQueueRow[]): {
  headers: string[]
  rows: string[][]
} {
  const headers = [
    ...REVIEW_QUEUE_COLUMN_KEYS.map((key) => REVIEW_QUEUE_COLUMN_LABELS[key]),
    'Match status',
  ]
  const exportRows = rows.map((row) => {
    const suggestion = row.suggestion
    return [
      row.description ?? '',
      row.vendor_item_code ?? '',
      row.qbo_vendor_id,
      row.pack_text ?? '',
      rowProductExportLabel(row),
      suggestion?.confidence ?? '',
      [row.document_reference_number, row.invoice_date].filter(Boolean).join(' '),
      row.quantity ?? '',
      row.unit_price ?? '',
      row.extended_price ?? '',
      row.cost_per_atomic_unit ?? '',
      (row.reasons ?? []).join('; '),
      row.decided_at ?? row.updated_at ?? '',
      row.updated_at ?? row.invoice_date ?? '',
      row.match_status,
    ]
  })
  return { headers, rows: exportRows }
}

export function rowProductExportLabel(row: PurchasingReviewQueueRow): string {
  if (row.product_name) return row.product_name
  if (row.meez_ingredient_name) return row.meez_ingredient_name
  if (row.suggestion) return suggestionOptionLabel(row.suggestion)
  return ''
}

export function reasonLabel(reason: string): string {
  return REASON_LABELS[reason] ?? reason
}

export function collectReviewReasonFilterValues(
  rows: ReadonlyArray<Pick<PurchasingReviewQueueRow, 'reasons'>>,
): string[] {
  const reasons = new Set<string>()
  for (const row of rows) {
    for (const reason of row.reasons ?? []) reasons.add(reason)
  }
  return [...reasons]
}

export type PurchasingReviewFilterContext = {
  getOptionId: (row: PurchasingReviewQueueRow) => string | null
  catalogOptionsForRow: (row: PurchasingReviewQueueRow) => SelectOption[]
  products?: ProductUomLookup[]
  meezRows?: MeezUomLookup[]
  meezNameRows?: Pick<PurchasingMeezIngredientRow, 'id' | 'name'>[]
}

function escapeBoundedTokenRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** Bracket section texts from a Meez ingredient display name. */
export function extractMeezBracketTexts(meezName: string): string[] {
  const texts: string[] = []
  const pattern = /\[([^\]]*)\]/g
  let match: RegExpExecArray | null
  while ((match = pattern.exec(meezName)) !== null) {
    texts.push(match[1] ?? '')
  }
  return texts
}

/** True when vendor item code is a bounded token inside bracket text only (len >= 3). */
export function vendorItemCodeMatchesMeezBracketIds(
  vendorItemCode: string | null | undefined,
  meezName: string,
): boolean {
  const code = (vendorItemCode ?? '').trim()
  if (code.length < 3) return false
  const bracketTexts = extractMeezBracketTexts(meezName)
  if (bracketTexts.length === 0) return false
  const pattern = new RegExp(
    `(?<![0-9A-Za-z])${escapeBoundedTokenRegex(code)}(?![0-9A-Za-z])`,
    'i',
  )
  return bracketTexts.some((text) => pattern.test(text))
}

export function resolveSelectedMeezIngredientName(
  row: PurchasingReviewQueueRow,
  optionId: string | null,
  catalogOptions: SelectOption[],
  meezNameRows: Pick<PurchasingMeezIngredientRow, 'id' | 'name'>[] = [],
): string | null {
  if (!optionId?.startsWith('meez:')) return null
  const meezId = optionId.slice('meez:'.length)
  const fromOption = catalogOptions.find((opt) => opt.id === optionId)?.label
  if (fromOption) return fromOption
  if (row.meez_ingredient_id === meezId && row.meez_ingredient_name) {
    return row.meez_ingredient_name
  }
  if (
    row.suggestion?.meez_ingredient_id === meezId &&
    row.suggestion.meez_ingredient_name
  ) {
    return row.suggestion.meez_ingredient_name
  }
  const fromCatalog = meezNameRows.find((entry) => entry.id === meezId)?.name
  return fromCatalog ?? null
}

export function rowHasItemIdMatch(
  row: PurchasingReviewQueueRow,
  optionId: string | null,
  catalogOptions: SelectOption[],
  meezNameRows: Pick<PurchasingMeezIngredientRow, 'id' | 'name'>[] = [],
): boolean {
  const meezName = resolveSelectedMeezIngredientName(row, optionId, catalogOptions, meezNameRows)
  if (!meezName) return false
  return vendorItemCodeMatchesMeezBracketIds(row.vendor_item_code, meezName)
}

export function queueRowReasonBadges(
  row: PurchasingReviewQueueRow,
  selection: ReviewSelection | null,
  optionId: string | null,
  catalogOptions: SelectOption[],
  products: ProductUomLookup[] = [],
  meezRows: MeezUomLookup[] = [],
  meezNameRows: Pick<PurchasingMeezIngredientRow, 'id' | 'name'>[] = [],
): string[] {
  const reasons = queueRowReasons(row, selection, products, meezRows)
  if (rowHasItemIdMatch(row, optionId, catalogOptions, meezNameRows)) {
    return [...reasons, ITEM_ID_MATCH_FLAG]
  }
  return reasons
}

export function reviewRowFilterReasonValues(
  row: PurchasingReviewQueueRow,
  ctx: PurchasingReviewFilterContext,
): string[] {
  const selection = selectionFromProductOptionId(row, ctx.getOptionId(row))
  const catalogOptions = ctx.catalogOptionsForRow(row)
  const badges = queueRowReasonBadges(
    row,
    selection,
    ctx.getOptionId(row),
    catalogOptions,
    ctx.products ?? [],
    ctx.meezRows ?? [],
    ctx.meezNameRows ?? [],
  )
  return badges
}

export function collectReviewReasonFilterValuesForRows(
  rows: ReadonlyArray<PurchasingReviewQueueRow>,
  ctx: PurchasingReviewFilterContext,
): string[] {
  const reasons = new Set<string>([ITEM_ID_MATCH_FLAG])
  for (const row of rows) {
    for (const reason of reviewRowFilterReasonValues(row, ctx)) {
      reasons.add(reason)
    }
  }
  return [...reasons]
}

const FLAG_BADGE_BASE =
  'inline-flex items-center whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset'
const FLAG_BADGE_NEUTRAL = `${FLAG_BADGE_BASE} ring-gray-200 bg-gray-50 text-gray-800`
const FLAG_BADGE_ATTENTION = `${FLAG_BADGE_BASE} ring-amber-200 bg-amber-50 text-amber-800`
const FLAG_BADGE_SUCCESS = `${FLAG_BADGE_BASE} ring-emerald-200 bg-emerald-50 text-emerald-800`

/** $/atomic display: $#,##0.0000 */
export function formatCostPerAtomic(value: string | number | null | undefined): string {
  if (value == null || value === '') return '—'
  const num = Number(value)
  if (!Number.isFinite(num)) return String(value)
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  })
}

/** Badge classes for reason chips shown on Review queue rows. */
export function flagBadgeClass(reason: string): string {
  switch (reason) {
    case 'needs_product':
    case 'missing_atomic_cost':
    case 'cost_outlier':
    case 'qty_assumed':
    case 'uom_conflict':
    case 'pack_drift':
    case 'alias_pack_unconfirmed':
      return FLAG_BADGE_ATTENTION
    case ITEM_ID_MATCH_FLAG:
      return FLAG_BADGE_SUCCESS
    default:
      return FLAG_BADGE_NEUTRAL
  }
}

export function rowIsBulkConfirmable(row: PurchasingReviewQueueRow): boolean {
  const reasons = row.reasons ?? []
  if (reasons.length === 0) return false
  return reasons.every((reason) => CONFIRMABLE_REASONS.has(reason))
}

function reasonBlocksApprove(
  reason: string,
  row: PurchasingReviewQueueRow,
  selection: ReviewSelection | null,
): boolean {
  if (BLOCKING_REASONS.has(reason)) return true
  if (reason === 'needs_product') {
    return vendorItemNeedsProduct(row) && !rowHasApprovableMatch(row, selection)
  }
  return !NON_BLOCKING_REASONS.has(reason)
}

/** True when Approve can lock in the row (match suggestion if needed + confirm). */
export function rowIsBulkApprovable(
  row: PurchasingReviewQueueRow,
  selection: ReviewSelection | null,
  products: ProductUomLookup[] = [],
  meezRows: MeezUomLookup[] = [],
): boolean {
  const reasons = queueRowReasons(row, selection, products, meezRows)
  if (reasons.length === 0) return false
  return !reasons.some((reason) => reasonBlocksApprove(reason, row, selection))
}

export type ReviewBulkRowAction = 'link' | 'link_product' | 'define_pack' | 'approve'

const REVIEW_BULK_ROW_ACTION_ORDER: ReviewBulkRowAction[] = [
  'link',
  'link_product',
  'define_pack',
  'approve',
]

export function selectionFromProductOptionId(
  row: PurchasingReviewQueueRow,
  optionId: string | null,
): ReviewSelection | null {
  if (!optionId) return null
  if (optionId.startsWith('product:')) {
    return { kind: 'product', productId: Number(optionId.slice('product:'.length)) }
  }
  if (optionId.startsWith('meez:')) {
    return { kind: 'meez', meezIngredientId: optionId.slice('meez:'.length) }
  }
  if (optionId.startsWith('new:') && row.suggestion?.new_product) {
    return { kind: 'new_product', newProduct: row.suggestion.new_product }
  }
  return null
}

export function canConfirmProductLink(
  row: PurchasingReviewQueueRow,
  optionId: string | null,
): boolean {
  if (!optionId) return false
  return buildMatchRequestFromRow(row, selectionFromProductOptionId(row, optionId)) != null
}

export function canClearProductSelection(optionId: string | null): boolean {
  return optionId != null
}

export function rowHasPersistedProductLink(row: PurchasingReviewQueueRow): boolean {
  return (
    row.product_id != null ||
    row.meez_ingredient_id != null ||
    row.match_status === 'confirmed' ||
    row.match_status === 'auto'
  )
}

/** Per-row quick actions shown in the modal sidebar and eligible for bulk when shared by all rows. */
export function reviewRowBulkActions(
  row: PurchasingReviewQueueRow,
  selection: ReviewSelection | null,
  segment: 'needs_review' | 'done' = 'needs_review',
  products: ProductUomLookup[] = [],
  meezRows: MeezUomLookup[] = [],
  optionId: string | null = null,
): ReviewBulkRowAction[] {
  if (segment === 'done') return []
  const actions: ReviewBulkRowAction[] = []
  const reasons = queueRowReasons(row, selection, products, meezRows)
  if (reasons.includes('needs_product') && canConfirmProductLink(row, optionId)) {
    actions.push('link')
  }
  if (reasons.includes('uom_conflict')) {
    if (
      row.product_id != null ||
      selection?.kind === 'product' ||
      !vendorItemNeedsProduct(row)
    ) {
      actions.push('define_pack')
    } else {
      actions.push('link_product')
    }
  }
  if (reasons.includes('missing_atomic_cost')) actions.push('define_pack')
  if (rowIsBulkApprovable(row, selection, products, meezRows)) actions.push('approve')
  return [...new Set(actions)]
}

export function reviewBulkActionsForAllRows(
  rows: PurchasingReviewQueueRow[],
  getSelection: (row: PurchasingReviewQueueRow) => ReviewSelection | null,
  segment: 'needs_review' | 'done' = 'needs_review',
  products: ProductUomLookup[] = [],
  meezRows: MeezUomLookup[] = [],
  getOptionId?: (row: PurchasingReviewQueueRow) => string | null,
): ReviewBulkRowAction[] {
  if (rows.length === 0) return []
  return REVIEW_BULK_ROW_ACTION_ORDER.filter((action) =>
    rows.every((row) =>
      reviewRowBulkActions(
        row,
        getSelection(row),
        segment,
        products,
        meezRows,
        getOptionId?.(row) ?? null,
      ).includes(action),
    ),
  )
}

export function reviewRowHasFlagActions(
  row: PurchasingReviewQueueRow,
  segment: 'needs_review' | 'done',
  selection: ReviewSelection | null = null,
  products: ProductUomLookup[] = [],
  meezRows: MeezUomLookup[] = [],
): boolean {
  if (segment === 'done') return true
  const reasons = queueRowReasons(row, selection, products, meezRows)
  return (
    reasons.includes('needs_product') ||
    reasons.includes('missing_atomic_cost') ||
    reasons.includes('qty_assumed') ||
    reasons.includes('cost_outlier') ||
    reasons.includes('uom_conflict')
  )
}

export function clampFocusedRowIndex(index: number, rowCount: number): number {
  if (rowCount <= 0) return -1
  return Math.min(Math.max(index, 0), rowCount - 1)
}

export function rowHasApprovableMatch(
  row: PurchasingReviewQueueRow,
  selection: ReviewSelection | null,
): boolean {
  return buildMatchRequestFromRow(row, selection) != null
}

export function queueRowToPurchaseLine(row: PurchasingReviewQueueRow): PurchasingPurchaseLineRow {
  return {
    id: row.id,
    document_id: row.document_id,
    vendor_item_id: row.vendor_item_id,
    product_id: row.product_id,
    qbo_entity_id: row.qbo_entity_id,
    qbo_vendor_id: row.qbo_vendor_id,
    invoice_date: row.invoice_date,
    quantity: row.quantity,
    quantity_uom: null,
    unit_price: row.unit_price,
    extended_price: row.extended_price,
    atomic_qty: null,
    cost_per_atomic_unit: row.cost_per_atomic_unit,
    review_status: row.review_status,
    review_flags: row.review_flags,
    vendor_item_code: row.vendor_item_code,
    description: row.description,
    pack_text: row.pack_text,
    source: row.source,
    pack_version_text: null,
    pack_effective_from: null,
    page_number: row.page_number,
    bbox: row.bbox,
    document_reference_number: row.document_reference_number,
  }
}

export function fixedColumnStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: width,
    boxSizing: 'border-box',
  }
}

export const REVIEW_CHECKBOX_COLUMN_WIDTH = 40

export const REVIEW_QUEUE_PINNED_COLUMNS = [
  'description',
  'vendor_item_code',
  'vendor',
] as const

export function isReviewPinnedColumn(columnKey: string): boolean {
  return (REVIEW_QUEUE_PINNED_COLUMNS as readonly string[]).includes(columnKey)
}

export function reviewPinnedStickyLeft(
  columnKey: string,
  visibleColumnKeys: readonly string[],
  getWidth: (key: string) => number,
): number {
  let left = REVIEW_CHECKBOX_COLUMN_WIDTH
  for (const key of visibleColumnKeys) {
    if (key === columnKey) return left
    if (isReviewPinnedColumn(key)) left += getWidth(key)
  }
  return left
}

/** Checkbox column + visible pinned data columns (name, item code, vendor). */
export function reviewPinnedRegionWidth(
  visibleColumnKeys: readonly string[],
  getWidth: (key: string) => number,
): number {
  let width = REVIEW_CHECKBOX_COLUMN_WIDTH
  for (const key of visibleColumnKeys) {
    if (isReviewPinnedColumn(key)) width += getWidth(key)
  }
  return width
}

export function lastVisibleReviewPinnedColumn(
  visibleColumnKeys: readonly string[],
): string | undefined {
  return [...visibleColumnKeys].reverse().find((key) => isReviewPinnedColumn(key))
}

export function reviewPinnedCellClassName(
  columnKey: string,
  visibleColumnKeys: readonly string[] = REVIEW_QUEUE_PINNED_COLUMNS,
): string {
  const classes = ['table-sticky-pinned', 'table-sticky-cell', 'overflow-hidden']
  if (columnKey === lastVisibleReviewPinnedColumn(visibleColumnKeys)) {
    classes.push('table-locked-edge', 'border-r', 'border-gray-200')
  }
  return classes.join(' ')
}

export function normalizeProductOptionLabel(label: string): string {
  return label.trim().split(/\s+/).filter(Boolean).join(' ').toLowerCase()
}

export function productConfirmButtonLabel(optionId: string | null): {
  idle: string
  busy: string
} {
  if (optionId?.startsWith('new:')) {
    return { idle: 'Create', busy: 'Creating…' }
  }
  return { idle: 'Link', busy: 'Linking…' }
}

export function preferExistingCatalogOption(
  optionId: string | null,
  options: { id: string; label: string }[],
  vendorItemCode?: string | null,
): string | null {
  if (!optionId || optionId.startsWith('product:')) return optionId

  const sourceLabel = optionId.startsWith('new:')
    ? optionId.slice('new:'.length)
    : (options.find((opt) => opt.id === optionId)?.label ?? null)
  if (optionId.startsWith('meez:')) {
    const meezLabel = options.find((opt) => opt.id === optionId)?.label
    if (
      meezLabel &&
      !meezSuggestionAutoSelectAllowed(meezLabel, vendorItemCode ?? undefined)
    ) {
      return null
    }
  }
  if (sourceLabel) {
    const normalized = normalizeProductOptionLabel(sourceLabel)
    const labelMatch = options.find(
      (opt) =>
        opt.id.startsWith('product:') &&
        normalizeProductOptionLabel(opt.label) === normalized,
    )
    if (labelMatch) return labelMatch.id
  }

  const code = vendorItemCode?.trim()
  if (code) {
    const codeMatches = options.filter(
      (opt) =>
        opt.id.startsWith('product:') &&
        vendorItemCodeMatchesMeezBracketIds(code, opt.label),
    )
    if (codeMatches.length === 1) return codeMatches[0]!.id
  }

  return optionId
}

export function suggestionOptionId(suggestion: PurchasingReviewQueueSuggestion): string | null {
  if (suggestion.proposed_product_id != null) {
    return `product:${suggestion.proposed_product_id}`
  }
  if (suggestion.meez_ingredient_id) {
    return `meez:${suggestion.meez_ingredient_id}`
  }
  if (suggestion.new_product) {
    return `new:${suggestion.new_product.name}`
  }
  return null
}

/** Default closed-field selection: Meez beats catalog product beats create-new. */
export function defaultSuggestionOptionId(
  suggestion: PurchasingReviewQueueSuggestion,
  vendorItemCode?: string | null,
  meezCatalog: Pick<PurchasingMeezIngredientRow, 'id' | 'name'>[] = [],
): string | null {
  if (suggestion.meez_ingredient_id) {
    const label = resolveMeezIngredientLabel(
      suggestion.meez_ingredient_id,
      meezCatalog,
      suggestion.meez_ingredient_name,
    )
    if (
      label &&
      meezSuggestionAutoSelectAllowed(label, vendorItemCode ?? undefined)
    ) {
      return `meez:${suggestion.meez_ingredient_id}`
    }
  }
  if (suggestion.proposed_product_id != null) {
    return `product:${suggestion.proposed_product_id}`
  }
  return null
}

export function suggestionOptionLabel(suggestion: PurchasingReviewQueueSuggestion): string {
  if (suggestion.product_name) return suggestion.product_name
  if (suggestion.meez_ingredient_name) return suggestion.meez_ingredient_name
  return 'Suggested match'
}

export function buildMatchRequestFromRow(
  row: PurchasingReviewQueueRow,
  selection: ReviewSelection | null,
): PurchasingVendorItemMatchRequest | null {
  if (selection) {
    if (selection.kind === 'product') {
      return { product_id: selection.productId }
    }
    if (selection.kind === 'meez') {
      return { meez_ingredient_id: selection.meezIngredientId }
    }
    return {
      new_product: selection.newProduct,
      meez_ingredient_id: selection.meezIngredientId,
    }
  }

  const suggestion = row.suggestion
  if (!suggestion) return null

  if (suggestion.meez_ingredient_id) {
    return { meez_ingredient_id: suggestion.meez_ingredient_id }
  }
  if (suggestion.proposed_product_id != null) {
    return { product_id: suggestion.proposed_product_id }
  }
  if (suggestion.new_product) {
    const payload: PurchasingVendorItemMatchRequest = { new_product: suggestion.new_product }
    if (suggestion.meez_ingredient_id) {
      payload.meez_ingredient_id = suggestion.meez_ingredient_id
    }
    return payload
  }
  return null
}

export function buildPackRulingPayload(
  ruling: 'correction' | 'vendor_change',
  line: PurchasingPurchaseLineRow,
  printedPack?: string | null,
): PurchasingPackRulingRequest {
  const packText = printedPack ?? line.pack_text ?? line.pack_version_text ?? undefined
  return {
    ruling,
    pack_text: packText,
    effective_invoice_date: line.invoice_date,
    raw_printed_pack: printedPack ?? line.pack_text ?? undefined,
  }
}

export function purchaseLineSourceLabel(source: string | null | undefined): string {
  if (!source) return '—'
  if (source === 'amazon_report') return 'Amazon report'
  if (source === 'qbo_purchase' || source.startsWith('qbo_purchase:')) {
    return source === 'qbo_purchase' ? 'QBO purchase' : `QBO purchase (${source.split(':').slice(1).join(':')})`
  }
  return source
}

export function isModelPathPurchaseLine(line: PurchasingPurchaseLineRow): boolean {
  if (line.source.startsWith('model:') || line.source.startsWith('model_strong:')) {
    return true
  }
  return line.review_flags.includes(MODEL_PATH_UNCONFIRMED_FLAG)
}

export function isPackDriftLine(line: PurchasingPurchaseLineRow): boolean {
  return line.review_flags.includes(PACK_DRIFT_FLAG)
}

export function isAliasPackLine(line: PurchasingPurchaseLineRow): boolean {
  return line.review_flags.includes(ALIAS_PACK_UNCONFIRMED_FLAG)
}

export function extractQboPurchaseEntityId(line: PurchasingPurchaseLineRow): string | null {
  if (line.qbo_entity_id) return line.qbo_entity_id
  if (line.document_id != null) return null
  if (line.source === 'qbo_purchase' || line.source.startsWith('qbo_purchase:')) {
    const suffix = line.source.split(':').slice(1).join(':')
    return suffix || null
  }
  return null
}

export function needsQboStrongExtraction(line: PurchasingPurchaseLineRow): boolean {
  return line.document_id == null && extractQboPurchaseEntityId(line) != null
}

export function reviewQueueSearchText(row: PurchasingReviewQueueRow): string {
  return [
    row.qbo_vendor_id,
    row.vendor_item_code,
    row.description,
    row.pack_text,
    row.product_name,
    row.meez_ingredient_name,
    row.document_reference_number,
    row.suggestion?.product_name,
    row.suggestion?.meez_ingredient_name,
    row.suggestion?.reasoning,
    ...(row.reasons ?? []),
  ]
    .filter(Boolean)
    .join(' ')
}

export function buildPurchasingReviewFilterFields(
  vendorOptions: FieldOption[],
  reasonOptions: FieldOption[],
  filterContext?: PurchasingReviewFilterContext,
): FieldDefinition[] {
  return [
    {
      key: 'vendor',
      label: 'Vendor',
      type: 'multiSelect',
      filterable: true,
      options: vendorOptions,
      accessor: (row: unknown) => (row as PurchasingReviewQueueRow).qbo_vendor_id,
    },
    {
      key: 'match_status',
      label: 'Match status',
      type: 'multiSelect',
      filterable: true,
      options: MATCH_STATUS_FILTER_OPTIONS,
      accessor: (row: unknown) => (row as PurchasingReviewQueueRow).match_status,
    },
    {
      key: 'reasons',
      label: 'Reasons',
      type: 'multiSelect',
      filterable: true,
      options: reasonOptions,
      accessor: (row: unknown) =>
        filterContext
          ? reviewRowFilterReasonValues(row as PurchasingReviewQueueRow, filterContext)
          : collectReviewReasonFilterValues([row as PurchasingReviewQueueRow]),
    },
    {
      key: 'has_suggestion',
      label: 'Has suggestion',
      type: 'select',
      filterable: true,
      options: HAS_SUGGESTION_FILTER_OPTIONS,
      accessor: (row: unknown) =>
        (row as PurchasingReviewQueueRow).suggestion ? 'yes' : null,
    },
  ]
}

export const REVIEW_QUEUE_DEFAULT_SORT = [{ field: 'vendor', direction: 'asc' as const }]

export const REVIEW_QUEUE_FIELD_DEFINITIONS: FieldDefinition[] = REVIEW_QUEUE_COLUMN_KEYS.map(
  (key) => ({
    key,
    label: REVIEW_QUEUE_COLUMN_LABELS[key],
    type: 'text',
    defaultVisible: key !== 'unit_price' && key !== 'amount' && key !== 'approved',
    sortable: key !== 'reasons' && key !== 'approved',
    locked: false,
  }),
)

export function applyVendorItemMatchLocally(
  rows: PurchasingReviewQueueRow[],
  vendorItemId: number,
  patch: Partial<PurchasingReviewQueueRow>,
): PurchasingReviewQueueRow[] {
  return rows.map((row) => {
    if (row.vendor_item_id !== vendorItemId) return row
    const merged = { ...row, ...patch }
    return { ...merged, reasons: buildReasonsForQueueRow(merged) }
  })
}

export function applyVendorItemUnlinkLocally(
  rows: PurchasingReviewQueueRow[],
  vendorItemId: number,
): PurchasingReviewQueueRow[] {
  return rows.map((row) => {
    if (row.vendor_item_id !== vendorItemId) return row
    const merged = {
      ...row,
      product_id: null,
      product_name: null,
      product_atomic_uom: null,
      meez_ingredient_id: null,
      meez_ingredient_name: null,
      match_status: 'pending' as const,
      review_flags: (row.review_flags ?? []).filter((flag) => flag !== 'uom_conflict'),
    }
    return { ...merged, reasons: buildReasonsForQueueRow(merged) }
  })
}

export function applyPackSavedLocally(
  rows: PurchasingReviewQueueRow[],
  vendorItemIds: ReadonlySet<number> | number[],
  packText: string,
): PurchasingReviewQueueRow[] {
  const ids = vendorItemIds instanceof Set ? vendorItemIds : new Set(vendorItemIds)
  return rows.map((row) => {
    if (!ids.has(row.vendor_item_id)) return row
    const reasons = (row.reasons ?? []).filter((reason) => reason !== 'missing_atomic_cost')
    const review_flags = (row.review_flags ?? []).filter((flag) => flag !== 'missing_atomic_cost')
    return { ...row, pack_text: packText, reasons, review_flags }
  })
}

export function applyUndoDoneLocally(
  rows: PurchasingReviewQueueRow[],
  lineId: number,
  vendorItemId: number,
  unlinked: boolean,
): PurchasingReviewQueueRow[] {
  return rows
    .filter((row) => row.id !== lineId)
    .map((row) => {
      if (!unlinked || row.vendor_item_id !== vendorItemId) return row
      const reasons = [...new Set([...(row.reasons ?? []), 'needs_product'])]
      return {
        ...row,
        product_id: null,
        product_name: null,
        product_atomic_uom: null,
        meez_ingredient_id: null,
        meez_ingredient_name: null,
        match_status: 'pending',
        review_status: 'flagged',
        reasons,
      }
    })
}

export function applyLinePatchLocally(
  rows: PurchasingReviewQueueRow[],
  lineId: number,
  patch: Partial<PurchasingReviewQueueRow>,
): PurchasingReviewQueueRow[] {
  return rows.map((row) => (row.id === lineId ? { ...row, ...patch } : row))
}

export function removeLineFromQueue(
  rows: PurchasingReviewQueueRow[],
  lineId: number,
): PurchasingReviewQueueRow[] {
  return removeLinesFromQueue(rows, [lineId])
}

export function removeLinesFromQueue(
  rows: PurchasingReviewQueueRow[],
  lineIds: Iterable<number>,
): PurchasingReviewQueueRow[] {
  const ids = lineIds instanceof Set ? lineIds : new Set(lineIds)
  return rows.filter((row) => !ids.has(row.id))
}

export type ReviewQueueRowPlacement = {
  row: PurchasingReviewQueueRow
  index: number
}

/** Insert snapshot rows at their original queue indices (stable order when indices tie). */
export function insertSnapshotRowsAtOriginalIndices(
  rows: PurchasingReviewQueueRow[],
  placements: readonly ReviewQueueRowPlacement[],
): PurchasingReviewQueueRow[] {
  if (placements.length === 0) return dedupeReviewQueueRows(rows)
  const snapshotIds = new Set(placements.map((placement) => placement.row.id))
  const withoutSnapshots = rows.filter((row) => !snapshotIds.has(row.id))
  const sorted = [...placements].sort((a, b) => a.index - b.index || a.row.id - b.row.id)
  let result = withoutSnapshots
  for (const { row, index } of sorted) {
    const clampedIndex = Math.min(Math.max(0, index), result.length)
    result = [...result.slice(0, clampedIndex), row, ...result.slice(clampedIndex)]
  }
  return dedupeReviewQueueRows(result)
}

export function collectVendorItemSnapshotPlacements(
  rows: readonly PurchasingReviewQueueRow[],
  vendorItemId: number,
): { snapshots: PurchasingReviewQueueRow[]; originalIndices: number[] } {
  const snapshots: PurchasingReviewQueueRow[] = []
  const originalIndices: number[] = []
  rows.forEach((row, index) => {
    if (row.vendor_item_id !== vendorItemId) return
    snapshots.push(row)
    originalIndices.push(index)
  })
  return { snapshots, originalIndices }
}

export function restoreLinesToQueue(
  rows: PurchasingReviewQueueRow[],
  snapshot: readonly PurchasingReviewQueueRow[],
  originalIndices?: readonly number[],
): PurchasingReviewQueueRow[] {
  if (
    originalIndices != null &&
    originalIndices.length === snapshot.length &&
    snapshot.length > 0
  ) {
    const placements = snapshot.map((row, i) => ({
      row,
      index: originalIndices[i]!,
    }))
    return insertSnapshotRowsAtOriginalIndices(rows, placements)
  }
  return dedupeReviewQueueRows([...snapshot, ...rows])
}

/** Skip persist UI when a newer link/undo epoch has replaced this attempt. */
export function shouldSkipProductLinkPersistApply(
  persistEpoch: number,
  currentEpoch: number,
): boolean {
  return persistEpoch !== currentEpoch
}

export function dedupeReviewQueueRows(
  rows: PurchasingReviewQueueRow[],
): PurchasingReviewQueueRow[] {
  const seen = new Set<number>()
  const deduped: PurchasingReviewQueueRow[] = []
  for (const row of rows) {
    if (seen.has(row.id)) continue
    seen.add(row.id)
    deduped.push(row)
  }
  return deduped
}
